import { DrmProviderInsert, StaticKeyDrmConfigEdit, StaticKeyLicenseConfig } from '@nativewaves/nw-cloud-sdk/content'

/**
 * A single PSSH entry. The backend models `pssh` as a `Record<GUID, Base64>`, but a record with
 * user-editable keys cannot be managed cleanly by react-hook-form, so the form holds it as an
 * array of entries and {@link toStaticKeyConfigEdit} converts it back to a record on submit.
 */
export type PsshEntry = {
  systemId: string
  value: string
}

/**
 * Form-friendly shape of `StaticKeyEncryptionDetails`. All scalar fields are non-nullable strings so
 * the inputs stay controlled; `pssh` is an array (see {@link PsshEntry}). There is no encryption
 * scheme: a static key always supports CENC, and additionally CBCS when an IV is provided. Mapped to
 * the SDK type on submit.
 */
export type StaticKeyConfigFormValues = {
  encryptionDetails: {
    keyId: string
    key: string
    iv: string
    skdUri: string
    pssh: PsshEntry[]
  }
  license: {
    widevineLicenseUrl: string
    playReadyLicenseUrl: string
    fairPlayLicenseUrl: string
  }
}

/**
 * The create form value shape. Identical to `DrmProviderInsert` except `staticKeyConfig` uses the
 * form-friendly {@link StaticKeyConfigFormValues} instead of the SDK's `StaticKeyDrmConfigEdit`.
 */
export type DrmProviderInsertFormValues = Omit<DrmProviderInsert, 'staticKeyConfig'> & {
  staticKeyConfig: StaticKeyConfigFormValues
}

export const GUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

/** Standard Base64 (RFC 4648) with optional padding. Note: the empty string also matches. */
export const BASE64_PATTERN = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/

/** Well-known DRM system UUIDs, used to pre-fill and label PSSH entries. */
export const WELL_KNOWN_DRM_SYSTEMS: ReadonlyArray<{ id: string; name: string }> = [
  { id: 'edef8ba9-79d6-4ace-a3c8-27dcd51d21ed', name: 'Widevine' },
  { id: '9a04f079-9840-4286-ab92-e65be0885f95', name: 'PlayReady' },
  { id: '94ce86fb-07ff-4f43-adb8-93d2fa968ca2', name: 'FairPlay' },
]

export const staticKeyConfigFormDefaults: StaticKeyConfigFormValues = {
  encryptionDetails: {
    keyId: '',
    key: '',
    iv: '',
    skdUri: '',
    pssh: [],
  },
  license: {
    widevineLicenseUrl: '',
    playReadyLicenseUrl: '',
    fairPlayLicenseUrl: '',
  },
}

const emptyToUndefined = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

/**
 * Maps the form values to the SDK `StaticKeyDrmConfigEdit` payload:
 * - `iv` is optional — when empty the provider supports CENC only; a value additionally enables CBCS,
 * - `pssh` entries with a blank UUID are dropped and the rest collapse into a record,
 * - the whole `license` object is omitted when none of the three URLs are set.
 */
export const toStaticKeyConfigEdit = (config: StaticKeyConfigFormValues): StaticKeyDrmConfigEdit => {
  const { encryptionDetails, license } = config

  const pssh = encryptionDetails.pssh.reduce<Record<string, string>>((acc, { systemId, value }) => {
    const id = systemId.trim()
    if (id) {
      acc[id] = value.trim()
    }
    return acc
  }, {})

  const licenseConfig: StaticKeyLicenseConfig = {
    widevineLicenseUrl: emptyToUndefined(license.widevineLicenseUrl),
    playReadyLicenseUrl: emptyToUndefined(license.playReadyLicenseUrl),
    fairPlayLicenseUrl: emptyToUndefined(license.fairPlayLicenseUrl),
  }
  const hasLicense = Object.values(licenseConfig).some((url) => url !== undefined)

  return {
    encryptionDetails: {
      keyId: encryptionDetails.keyId.trim(),
      key: encryptionDetails.key.trim(),
      iv: emptyToUndefined(encryptionDetails.iv),
      skdUri: emptyToUndefined(encryptionDetails.skdUri),
      pssh,
    },
    ...(hasLicense ? { license: licenseConfig } : {}),
  }
}
